/**
 *  @fileOverview Custom select handler
 *
 *  @author       Peter Schmiz <peter.schmiz@possible.com>
 *
 *  @requires     NPM:lodash
 *  @requires     NPM:detect-it
 */

import forEach from 'lodash/forEach';
import debounce from 'lodash/debounce';
import detectIt from 'detect-it';

const ACTIVE_CLASS = 'select--active';
const ACTIVATE_KEYCODES = [32, 38, 40];
const DEACTIVATE_KEYCODES = [27];
const SELECT_KEYCODES = [13, 32];
const NAVIGATION_KEYCODES = [38, 40];

const ITEM_HEIGHT = 70;

const isIEorEdge = () => (
  /MSIE 10/i.test(navigator.userAgent) ||
  /MSIE 9/i.test(navigator.userAgent) ||
  /rv:11.0/i.test(navigator.userAgent) ||
  /Edge\/\d./i.test(navigator.userAgent)
);

class CustomSelect {
  constructor(el) {
    if (!(typeof el && el.nodeName)) {
      throw TypeError('Constructor parameter should be a valid DOM element!');
    }

    this.isSelectActive = false;
    this.selectedIndex = -1;
    this.navigateIndex = -1;

    this.dropDownDirection = 'down';

    this.select = el;

    this.initDOMElements();
    this.initBindings();
    this.checkSpace();
  }

  initDOMElements() {
    this.selected = this.select.querySelector('[data-custom-select-selected]');
    this.nativeSelect = this.select.querySelector('[data-custom-select-native-input]');
    this.options = this.select.querySelectorAll('[data-custom-select-option]');
  }

  initBindings() {
    this.select.addEventListener('click', () => {
      this.toggleOptions();
    }, detectIt.passiveEvents ? {
      capture: false,
      passive: true,
    } : false);

    this.select.addEventListener('keydown', (e) => {
      if (
        [...ACTIVATE_KEYCODES, ...SELECT_KEYCODES].includes(e.keyCode) ||
        (e.keyCode === 9 && this.isSelectActive === true)
      ) {
        e.preventDefault();
      }
      if (ACTIVATE_KEYCODES.includes(e.keyCode) && this.isSelectActive === false) {
        this.openOptions();
      } else if (DEACTIVATE_KEYCODES.includes(e.keyCode) && this.isSelectActive === true) {
        this.closeOptions();
        this.resetOptions();
      } else if (SELECT_KEYCODES.includes(e.keyCode) && this.isSelectActive === true) {
        this.selectOption(this.options[this.navigateIndex]);
        this.closeOptions();
      } else if (NAVIGATION_KEYCODES.includes(e.keyCode) && this.isSelectActive === true) {
        this.navigateOptions(e.keyCode);
      }
    }, false);

    this.select.addEventListener('blur', (e) => {
      if (!isIEorEdge()) {
        if (
          e.relatedTarget === null ||
          (
            e.relatedTarget !== null && e.relatedTarget && e.relatedTarget.getAttribute('data-custom-select') !== null
          )
        ) {
          this.closeOptions();
        }
      }
    }, false);

    this.nativeSelect.addEventListener('blur', () => {
      this.updateSelectFromNative();
    }, false);

    this.nativeSelect.addEventListener('change', () => {
      this.updateSelectFromNative();
    }, false);

    window.addEventListener(
      'resize',
      debounce(() => {
        this.checkSpace();
      }, 200),
      detectIt.passiveEvents ? { capture: false, passive: true } : false
    );

    window.addEventListener(
      'scroll',
      debounce(() => {
        this.checkSpace();
      }, 200),
      detectIt.passiveEvents ? { capture: false, passive: true } : false
    );

    forEach(this.select.querySelectorAll('[data-custom-select-options] [data-custom-select-option]'), (item) => {
      item.addEventListener('click', (e) => {
        this.selectOption(e.currentTarget);
      }, detectIt.passiveEvents ? {
        capture: false,
        passive: true,
      } : false);
    });
  }

  updateSelectFromNative() {
    const option = this.nativeSelect.querySelector('option:checked');
    const value = option.getAttribute('value');
    this.updateSelected(option.innerText);
    if (detectIt.deviceType !== 'mouseOnly') {
      if (this.changeCallback && typeof this.changeCallback === 'function') {
        this.changeCallback(this.select, value);
      }
    }
  }

  checkSpace() {
    const listHeight = this.options.length * ITEM_HEIGHT;
    const selectOffsetY = this.select.getBoundingClientRect().top + window.scrollY;

    if ((window.innerHeight - selectOffsetY - ITEM_HEIGHT) + window.scrollY < listHeight) {
      this.dropDownDirection = 'up';
    } else {
      this.dropDownDirection = 'down';
    }

    this.select.classList.remove('direction-up');
    this.select.classList.remove('direction-down');
    this.select.classList.add(`direction-${this.dropDownDirection}`);
  }

  toggleOptions() {
    if (this.select.classList.contains(ACTIVE_CLASS)) {
      this.closeOptions();
    } else {
      this.openOptions();
    }
  }

  openOptions() {
    this.isSelectActive = true;
    this.select.setAttribute('aria-expanded', true);
    this.select.classList.add(ACTIVE_CLASS);
    if (this.selectedIndex > -1) {
      this.options[this.selectedIndex].classList.add('selected');
    }
    this.navigateIndex = this.selectedIndex;

    if (isIEorEdge()) {
      this.fixIEOptions();
    }
  }

  fixIEOptions() {
    forEach(this.options, (option) => {
      /* eslint-disable no-param-reassign */
      option.style.display = 'none';
      /* eslint-enable no-param-reassign */
    });

    window.setTimeout(() => {
      forEach(this.options, (option) => {
        /* eslint-disable no-param-reassign */
        option.style.display = 'flex';
        /* eslint-enable no-param-reassign */
      });
    }, 10);
  }

  closeOptions() {
    this.isSelectActive = false;
    this.select.setAttribute('aria-expanded', false);
    this.select.classList.remove(ACTIVE_CLASS);
  }

  navigateOptions(keyCode) {
    let newIndex = this.navigateIndex + (keyCode === 38 ? -1 : 1);

    if (newIndex < 0) {
      newIndex = 0;
    }

    if (newIndex > this.options.length - 1) {
      newIndex = this.options.length - 1;
    }

    this.resetOptions(true);
    this.navigateIndex = newIndex;
    this.options[this.navigateIndex].classList.add('hover');
  }

  selectOption(item) {
    const value = item.getAttribute('data-custom-select-option');
    const option = this.nativeSelect.querySelector(`option[value='${value}']`);

    this.resetOptions();
    this.resetSelection();
    item.classList.add('selected');

    if (option !== null) {
      option.setAttribute('selected', true);
      this.nativeSelect.value = value;
      this.select.setAttribute('aria-activedescendant', value);
      this.updateSelected(option.innerText);

      if (this.nativeSelect.options && this.nativeSelect[0].value === '') {
        this.selectedIndex = this.nativeSelect.selectedIndex - 1;
      } else {
        this.selectedIndex = this.nativeSelect.selectedIndex;
      }
      this.navigateIndex = this.selectedIndex;

      if (this.changeCallback && typeof this.changeCallback === 'function') {
        this.changeCallback(this.select, value);
      }
    }
  }

  resetOptions(onlyHover = false) {
    forEach(this.options, (option) => {
      if (onlyHover === false) {
        option.classList.remove('selected');
      }
      option.classList.remove('hover');
    });
  }

  resetSelection(customLabel) {
    this.resetOptions();
    this.updateSelected(customLabel || 'please select');
    forEach(this.nativeSelect.querySelectorAll('option'), (option) => {
      option.removeAttribute('selected');
      /* eslint-disable no-param-reassign */
      option.selected = false;
      /* eslint-enable no-param-reassign */
    });
  }

  updateSelected(label) {
    this.selected.querySelector('.label').innerHTML = label;
  }

  setChangeCallback(callback = null) {
    if (callback && typeof callback === 'function') {
      this.changeCallback = callback;
    }
  }
}

export default CustomSelect;



// WEBPACK FOOTER //
// ./src/js/modules/custom-select.js